import h from 'hyperscript';
import { type TemplateResult } from 'lit-html';

import type { StyleClass } from '@disy/lib/add-style-class';
import { addStyleClass } from '@disy/lib/add-style-class';
import type { Content, LitContent } from '@disy/ui/content';
import { normalizeContent } from '@disy/ui/content';

import './separator.css';

interface SeparatorOptions {
  dark?: boolean;
  styleClass?: StyleClass;
}

type OrientedSeparatorOptions = SeparatorOptions & { orientation?: 'horizontal' | 'vertical' };

function applyOptions(el: HTMLElement, { dark = false, styleClass }: SeparatorOptions = {}) {
  el.classList.toggle('d-separator-dark', dark);
  if (styleClass) {
    addStyleClass(el, styleClass);
  }
  return el;
}

/**
 * Returns a List of basic Content with separators between its initial given elements
 *
 * @param [elements] - List of html elements between whose entries separators are inserted
 * @param options - SeparatorOptions for the separator creation
 */
export function separateElements(elements: Content, options?: OrientedSeparatorOptions): (string | Node)[];
export function separateElements(
  elements: LitContent,
  options?: OrientedSeparatorOptions,
): (string | Node | TemplateResult)[];
export function separateElements(elements: LitContent, options?: OrientedSeparatorOptions) {
  const separatorFunction = options?.orientation === 'vertical' ? createVerticalSeparator : createHorizontalSeparator;
  const result = [];
  for (const element of normalizeContent(elements)) {
    if (element) {
      if (result.length) {
        result.push(separatorFunction(options));
      }
      result.push(element);
    }
  }
  return result;
}

/**
 * Creates a horizontal separator.
 *
 * @param [options]
 * @param [options.dark] - Whether to toggle the dark variant
 * @return The separator element
 */
export function createHorizontalSeparator(options?: SeparatorOptions) {
  return applyOptions(h('hr'), options);
}

/**
 * Creates a vertical separator.
 *
 * @param [options]
 * @param [options.dark] - Whether to toggle the dark variant
 * @param [options.styleClass] - An additional CSS class to add
 * @return The separator element
 */
export function createVerticalSeparator(options?: SeparatorOptions) {
  return applyOptions(h('div', { attrs: { role: 'separator', 'aria-orientation': 'vertical' } }), options);
}

export { createHorizontalSeparator as hr, createVerticalSeparator as vr };
